import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  Play, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  Volume1, 
  VolumeX, 
  Shuffle, 
  Repeat, 
  X, 
  Heart,
  Music,
  Smartphone,
  Monitor,
  Tv,
  RefreshCw
} from 'lucide-react';
import { useSpotify } from '../context/SpotifyContext';
import { apiCall } from '../services/api';

const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const GlobalMediaPlayerBar = () => {
  const {
    currentTrack,
    setCurrentTrack,
    isPlaying,
    setIsPlaying,
    accessToken,
    showPlayerBar,
    setShowPlayerBar,
    setShowNowPlaying,
    playerDismissed,
    setPlayerDismissed,
    progressMs,
    setProgressMs,
    durationMs,
    volume: globalVolume,
    setVolume: setGlobalVolume,
    shuffleState: globalShuffle,
    setShuffleState: setGlobalShuffle,
    repeatState: globalRepeat,
    setRepeatState: setGlobalRepeat,
  } = useSpotify();

  const [volume, setVolume] = useState(globalVolume || 50);
  const [shuffle, setShuffle] = useState(globalShuffle || false);
  const [repeat, setRepeat] = useState(globalRepeat || 'off');
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [devices, setDevices] = useState([]);
  const [activeDevice, setActiveDevice] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const deviceSelectorRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const lastShuffleUpdateRef = useRef(0);
  const lastRepeatUpdateRef = useRef(0);

  // Sync local volume with global volume
  useEffect(() => {
    if (globalVolume !== undefined && globalVolume !== volume) {
      setVolume(globalVolume);
    }
  }, [globalVolume]);

  // Sync local shuffle with global shuffle (but not immediately after user action)
  useEffect(() => {
    if (globalShuffle !== undefined) {
      const timeSinceLastUpdate = Date.now() - lastShuffleUpdateRef.current;
      // Only sync from API if it's been more than 3 seconds since user clicked
      if (timeSinceLastUpdate > 3000) {
        console.log('🔀 Syncing shuffle state from Spotify:', globalShuffle);
        setShuffle(globalShuffle);
      } else {
        console.log('🔀 Ignoring shuffle sync (too soon after user action)');
      }
    }
  }, [globalShuffle]);

  // Sync local repeat with global repeat (but not immediately after user action)
  useEffect(() => {
    if (globalRepeat !== undefined) {
      const timeSinceLastUpdate = Date.now() - lastRepeatUpdateRef.current;
      // Only sync from API if it's been more than 3 seconds since user clicked
      if (timeSinceLastUpdate > 3000) {
        console.log('🔁 Syncing repeat state from Spotify:', globalRepeat);
        setRepeat(globalRepeat);
      } else {
        console.log('🔁 Ignoring repeat sync (too soon after user action)');
      }
    }
  }, [globalRepeat]);

  // Smooth progress interpolation - update every second when playing
  useEffect(() => {
    if (isPlaying && durationMs > 0) {
      progressIntervalRef.current = setInterval(() => {
        setProgressMs(prev => {
          const next = prev + 1000;
          // Don't exceed duration
          return next <= durationMs ? next : durationMs;
        });
      }, 1000);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, durationMs, setProgressMs]);

  // Close device selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (deviceSelectorRef.current && !deviceSelectorRef.current.contains(event.target)) {
        setShowDeviceSelector(false);
      }
    };

    if (showDeviceSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDeviceSelector]);

  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Fetch available Spotify devices
  const fetchDevices = useCallback(async () => {
    if (!accessToken) return;
    setLoadingDevices(true);
    try {
      const res = await apiCall(`/spotify/devices?access_token=${encodeURIComponent(accessToken)}`);
      setDevices(res.devices || []);
      const active = res.devices?.find(d => d.is_active);
      if (active) setActiveDevice(active);
      return res.devices || [];
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      return [];
    } finally {
      setLoadingDevices(false);
    }
  }, [accessToken]);

  // Transfer playback to a specific device
  const transferToDevice = useCallback(async (deviceId) => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/transfer?access_token=${encodeURIComponent(accessToken)}&device_id=${deviceId}&play=true`, { method: 'POST' });
      setShowDeviceSelector(false);
      // Refresh devices after transfer
      setTimeout(fetchDevices, 1000);
    } catch (err) {
      console.error('Failed to transfer playback:', err);
    }
  }, [accessToken, fetchDevices]);

  // Auto-connect to current computer
  const connectToThisComputer = useCallback(async () => {
    if (!accessToken) return;
    setLoadingDevices(true);
    try {
      const deviceList = await fetchDevices();
      
      // Priority: Web Player > any Computer > first available device
      const webPlayer = deviceList.find(d => d.type === 'Computer' && d.name?.toLowerCase().includes('web'));
      const computer = deviceList.find(d => d.type === 'Computer');
      const targetDevice = webPlayer || computer || deviceList[0];
      
      if (targetDevice) {
        if (!targetDevice.is_active) {
          await transferToDevice(targetDevice.id);
        }
        setActiveDevice(targetDevice);
        setShowDeviceSelector(false);
      } else {
        // No devices found, show the dropdown
        setShowDeviceSelector(true);
      }
    } catch (err) {
      console.error('Failed to connect to this computer:', err);
      setShowDeviceSelector(true);
    }
  }, [accessToken, fetchDevices, transferToDevice]);

  // Fetch devices when component mounts or when opening device selector
  useEffect(() => {
    if (showDeviceSelector && accessToken) {
      fetchDevices();
    }
  }, [showDeviceSelector, accessToken, fetchDevices]);

  const togglePlayback = useCallback(async () => {
    if (!accessToken) return;
    try {
      if (isPlaying) {
        await apiCall(`/spotify/playback/pause?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
        setIsPlaying(false);
      } else {
        await apiCall(`/spotify/playback/play?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Toggle playback error:', error);
    }
  }, [accessToken, isPlaying, setIsPlaying]);

  const skipNext = useCallback(async () => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/next?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
    } catch (error) {
      console.error('Skip next error:', error);
    }
  }, [accessToken]);

  const skipPrevious = useCallback(async () => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/previous?access_token=${encodeURIComponent(accessToken)}`, { method: 'POST' });
    } catch (error) {
      console.error('Skip previous error:', error);
    }
  }, [accessToken]);

  const toggleShuffle = useCallback(async () => {
    if (!accessToken) return;
    try {
      const newState = !shuffle;
      console.log('🔀 Toggling shuffle:', shuffle, '→', newState);
      
      // Record timestamp to prevent immediate sync override
      lastShuffleUpdateRef.current = Date.now();
      
      // Update local state immediately for instant UI feedback
      setShuffle(newState);
      setGlobalShuffle(newState);
      
      // Make API call
      await apiCall(`/spotify/playback/shuffle?access_token=${encodeURIComponent(accessToken)}&state=${newState}`, { method: 'POST' });
      console.log('✅ Shuffle API call successful');
    } catch (error) {
      console.error('❌ Toggle shuffle error:', error);
      // Revert on error
      setShuffle(!shuffle);
      setGlobalShuffle(!shuffle);
    }
  }, [accessToken, shuffle, setGlobalShuffle]);

  const toggleRepeat = useCallback(async () => {
    if (!accessToken) return;
    try {
      const states = ['off', 'context', 'track'];
      const currentIndex = states.indexOf(repeat);
      const newState = states[(currentIndex + 1) % 3];
      console.log('🔁 Toggling repeat:', repeat, '→', newState);
      
      // Record timestamp to prevent immediate sync override
      lastRepeatUpdateRef.current = Date.now();
      
      // Update local state immediately for instant UI feedback
      setRepeat(newState);
      setGlobalRepeat(newState);
      
      // Make API call
      await apiCall(`/spotify/playback/repeat?access_token=${encodeURIComponent(accessToken)}&state=${newState}`, { method: 'POST' });
      console.log('✅ Repeat API call successful');
    } catch (error) {
      console.error('❌ Toggle repeat error:', error);
      // Revert on error
      setRepeat(repeat);
      setGlobalRepeat(repeat);
    }
  }, [accessToken, repeat, setGlobalRepeat]);

  const seekToPosition = useCallback(async (positionMs) => {
    if (!accessToken) return;
    try {
      await apiCall(`/spotify/playback/seek?access_token=${encodeURIComponent(accessToken)}&position_ms=${positionMs}`, { method: 'POST' });
      setProgressMs(positionMs);
    } catch (error) {
      console.error('Seek error:', error);
    }
  }, [accessToken, setProgressMs]);

  // Progress bar drag handlers
  const handleProgressMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const updateProgress = (clientX) => {
      if (!progressBarRef.current) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setDragProgress(percent * durationMs);
    };
    
    updateProgress(e.clientX);
    
    const handleMouseMove = (e) => {
      updateProgress(e.clientX);
    };
    
    const handleMouseUp = async (e) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (progressBarRef.current) {
        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const positionMs = Math.floor(percent * durationMs);
        await seekToPosition(positionMs);
      }
      
      setIsDragging(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [durationMs, seekToPosition]);

  // Touch support for mobile
  const handleProgressTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const touch = e.touches[0];
    const updateProgress = (clientX) => {
      if (!progressBarRef.current) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setDragProgress(percent * durationMs);
    };
    
    updateProgress(touch.clientX);
    
    const handleTouchMove = (e) => {
      updateProgress(e.touches[0].clientX);
    };
    
    const handleTouchEnd = async (e) => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      
      if (progressBarRef.current && e.changedTouches[0]) {
        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.changedTouches[0].clientX - rect.left) / rect.width));
        const positionMs = Math.floor(percent * durationMs);
        await seekToPosition(positionMs);
      }
      
      setIsDragging(false);
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [durationMs, seekToPosition]);

  // Display progress (use drag progress when dragging, otherwise actual progress)
  const displayProgressMs = isDragging ? dragProgress : progressMs;
  const displayProgressPercent = durationMs > 0 ? (displayProgressMs / durationMs) * 100 : 0;

  const setVolumeLevel = useCallback(async (level) => {
    if (!accessToken) return;
    setVolume(level);
    setGlobalVolume(level); // Sync with global state
    try {
      await apiCall(`/spotify/playback/volume?access_token=${encodeURIComponent(accessToken)}&volume=${level}`, { method: 'POST' });
    } catch (error) {
      console.error('Volume error:', error);
    }
  }, [accessToken, setGlobalVolume]);

  // If player was dismissed, don't show anything until a new song is played
  if (playerDismissed) {
    return null;
  }

  // If player bar is hidden but we have a track, show mini player tab
  if (!showPlayerBar && currentTrack) {
    return (
      <Portal>
        <div 
          className="fixed bottom-4 right-4 flex items-center gap-2"
          style={{ zIndex: 99999 }}
        >
          <button
            onClick={() => setShowPlayerBar(true)}
            className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-full shadow-lg shadow-green-500/30 hover:from-green-500 hover:to-green-400 transition-all hover:scale-105 group"
            data-testid="mini-player-tab"
          >
            {/* Album Art */}
            <div className="w-10 h-10 rounded-lg overflow-hidden shadow-md flex-shrink-0 ring-2 ring-white/30">
              {currentTrack.image ? (
                <img src={currentTrack.image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-green-700 flex items-center justify-center">
                  <Music size={18} className="text-white/70" />
                </div>
              )}
            </div>
            
            {/* Playing indicator + Song name */}
            <div className="flex items-center gap-2">
              {isPlaying ? (
                <div className="flex items-center gap-0.5">
                  <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" />
                  <div className="w-0.5 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
              ) : (
                <Play size={16} fill="white" className="text-white" />
              )}
            </div>
            
            <div className="flex flex-col items-start">
              <span className="text-[10px] uppercase tracking-wider text-white/70 font-medium">Now Playing</span>
              <span className="text-sm font-semibold max-w-[180px] truncate">{currentTrack.name}</span>
            </div>
          </button>
          
          {/* Close X Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPlayerDismissed(true);
              setCurrentTrack(null);
              setShowPlayerBar(false);
            }}
            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all hover:scale-110"
            title="Close player"
            data-testid="close-mini-player"
          >
            <X size={20} strokeWidth={3} />
          </button>
        </div>
      </Portal>
    );
  }

  if (!showPlayerBar || !currentTrack) {
    return null;
  }

  const progressPercent = durationMs > 0 ? (progressMs / durationMs) * 100 : 0;

  return (
    <Portal>
      <div 
        className="fixed bottom-0 left-0 right-0 bg-black backdrop-blur-xl border-t border-white/30 shadow-[0_-4px_40px_rgba(255,255,255,0.1)]"
        style={{ zIndex: 99999 }}
        data-testid="global-spotify-player-bar"
      >
        <div className="px-6 py-3 relative">
          {/* Close/Collapse Button - Right side, vertically centered */}
          <button
            onClick={() => setShowPlayerBar(false)}
            className="absolute top-1/2 -translate-y-1/2 right-4 p-2 bg-red-600 hover:bg-red-500 rounded-full transition-colors z-10 shadow-lg"
            title="Close Player"
            data-testid="close-player-bar"
          >
            <X size={20} className="text-white" strokeWidth={3} />
          </button>
          
          {/* Main container - centered with max width */}
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-8 pr-12">
            
            {/* Left: Album Art & Track Info */}
            <div className="flex items-center gap-4 w-72 flex-shrink-0">
              <div 
                onClick={() => setShowNowPlaying(true)}
                className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 shadow-xl ring-2 ring-white/40 bg-black cursor-pointer hover:ring-green-400/60 transition-all"
                data-testid="global-album-art-click"
                title="View Now Playing"
              >
                {currentTrack.image ? (
                  <img 
                    src={currentTrack.image} 
                    alt={currentTrack.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                    <Music size={24} className="text-white/70" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setShowNowPlaying(true)}>
                <h4 
                  className="text-white font-semibold truncate text-sm hover:underline"
                >
                  {currentTrack.name}
                </h4>
                <p className="text-gray-400 text-xs truncate">{currentTrack.artists}</p>
              </div>
              <button className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <Heart size={18} className="text-gray-400 hover:text-white" />
              </button>
            </div>

            {/* Center: Controls & Progress Bar */}
            <div className="flex flex-col items-center gap-2 flex-1 max-w-xl">
              {/* Playback Controls */}
              <div className="flex items-center gap-3">
                {/* Shuffle Button */}
                <button 
                  onClick={toggleShuffle}
                  data-testid="global-shuffle-btn"
                  className={`p-2 rounded-full transition-all hover:scale-110 ${
                    shuffle 
                      ? 'text-green-500 bg-green-500/10' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  title={shuffle ? 'Shuffle On' : 'Shuffle Off'}
                >
                  <Shuffle size={18} />
                </button>
                
                {/* Previous Button */}
                <button 
                  onClick={skipPrevious}
                  data-testid="global-previous-btn"
                  className="p-3 hover:bg-white/10 rounded-full transition-all text-white hover:scale-110 active:scale-95"
                  title="Previous Track"
                >
                  <SkipBack size={24} fill="currentColor" />
                </button>
                
                {/* Play/Pause Button */}
                <button 
                  onClick={togglePlayback}
                  data-testid="global-playpause-btn"
                  className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-lg shadow-green-500/40"
                >
                  {isPlaying ? (
                    <div className="flex gap-1">
                      <div className="w-1.5 h-5 bg-white rounded-full" />
                      <div className="w-1.5 h-5 bg-white rounded-full" />
                    </div>
                  ) : (
                    <Play size={22} className="text-white ml-0.5" fill="white" />
                  )}
                </button>
                
                {/* Next Button */}
                <button 
                  onClick={skipNext}
                  data-testid="global-next-btn"
                  className="p-3 hover:bg-white/10 rounded-full transition-all text-white hover:scale-110 active:scale-95"
                  title="Next Track"
                >
                  <SkipForward size={24} fill="currentColor" />
                </button>
                
                {/* Repeat Button */}
                <button 
                  onClick={toggleRepeat}
                  data-testid="global-repeat-btn"
                  className={`p-2 rounded-full transition-all hover:scale-110 relative ${
                    repeat !== 'off' 
                      ? 'text-green-500 bg-green-500/10' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  title={repeat === 'off' ? 'Repeat Off' : repeat === 'context' ? 'Repeat All' : 'Repeat One'}
                >
                  <Repeat size={18} />
                  {repeat === 'track' && (
                    <span className="absolute -top-1 -right-1 text-[10px] font-bold text-green-500 bg-black rounded-full w-4 h-4 flex items-center justify-center">1</span>
                  )}
                </button>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full flex items-center gap-3">
                <span className="text-xs text-gray-400 w-10 text-right font-mono tabular-nums">
                  {formatDuration(displayProgressMs)}
                </span>
                <div 
                  ref={progressBarRef}
                  className={`flex-1 h-2 bg-gray-700/80 rounded-full cursor-pointer group relative select-none ${isDragging ? 'h-3' : ''}`}
                  onMouseDown={handleProgressMouseDown}
                  onTouchStart={handleProgressTouchStart}
                  data-testid="progress-bar"
                >
                  <div 
                    className="h-full bg-green-500 rounded-full relative"
                    style={{ width: `${displayProgressPercent}%` }}
                  >
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-all ${isDragging ? 'scale-125 opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
                  </div>
                </div>
                <span className="text-xs text-gray-400 w-10 font-mono tabular-nums">
                  {formatDuration(durationMs)}
                </span>
              </div>
            </div>

            {/* Right: Volume Control & Device Selector */}
            <div className="flex items-center gap-2 flex-shrink-0 justify-end pr-8">
              {/* Volume Control */}
              <button 
                onClick={() => setVolumeLevel(volume === 0 ? 50 : 0)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title={volume === 0 ? 'Unmute' : 'Mute'}
              >
                {volume === 0 ? (
                  <VolumeX size={20} className="text-gray-400" />
                ) : volume < 50 ? (
                  <Volume1 size={20} className="text-gray-400" />
                ) : (
                  <Volume2 size={20} className="text-gray-400" />
                )}
              </button>
              <div className="relative w-20 h-6 flex items-center">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => setVolumeLevel(parseInt(e.target.value))}
                  data-testid="volume-slider"
                  className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-3
                    [&::-webkit-slider-thumb]:h-3
                    [&::-webkit-slider-thumb]:bg-white
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-webkit-slider-thumb]:hover:scale-125
                    [&::-webkit-slider-thumb]:transition-transform
                    [&::-moz-range-thumb]:appearance-none
                    [&::-moz-range-thumb]:w-3
                    [&::-moz-range-thumb]:h-3
                    [&::-moz-range-thumb]:bg-white
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:border-0
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:cursor-pointer
                    [&::-moz-range-thumb]:hover:scale-125
                    [&::-moz-range-thumb]:transition-transform"
                  style={{
                    background: `linear-gradient(to right, #22c55e ${volume}%, #374151 ${volume}%)`
                  }}
                />
              </div>

              {/* Device Selector Button */}
              <div className="relative" ref={deviceSelectorRef}>
                <button
                  onClick={connectToThisComputer}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setShowDeviceSelector(!showDeviceSelector);
                  }}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors group relative"
                  title="Connect to this computer (right-click for all devices)"
                  data-testid="device-selector-btn"
                >
                  <Monitor size={20} className={`${activeDevice ? 'text-green-400' : 'text-gray-400'} group-hover:text-white`} />
                  {activeDevice && (
                    <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border border-black" />
                  )}
                </button>

                {/* Device Selector Dropdown */}
                {showDeviceSelector && (
                  <div className="absolute bottom-full right-0 mb-2 w-80 bg-gray-900 border border-white/20 rounded-xl shadow-2xl overflow-hidden z-50">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                      <h3 className="text-white font-semibold text-sm">Connect to a device</h3>
                      <button
                        onClick={() => fetchDevices()}
                        disabled={loadingDevices}
                        className="p-1.5 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
                        title="Refresh devices"
                      >
                        <RefreshCw size={14} className={`text-gray-400 ${loadingDevices ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    {/* Device List */}
                    <div className="max-h-64 overflow-y-auto p-2">
                      {loadingDevices ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        </div>
                      ) : devices.length > 0 ? (
                        devices.map((device) => (
                          <button
                            key={device.id}
                            onClick={() => transferToDevice(device.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors mb-1 ${
                              device.is_active
                                ? 'bg-green-500/20 border border-green-500/50'
                                : 'hover:bg-white/5'
                            }`}
                          >
                            {/* Device Icon */}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              device.is_active ? 'bg-green-500' : 'bg-gray-700'
                            }`}>
                              {device.type === 'Computer' ? (
                                <Monitor size={18} className="text-white" />
                              ) : device.type === 'Smartphone' ? (
                                <Smartphone size={18} className="text-white" />
                              ) : (
                                <Tv size={18} className="text-white" />
                              )}
                            </div>

                            {/* Device Info */}
                            <div className="flex-1 text-left">
                              <p className="text-white font-medium text-sm">{device.name}</p>
                              <p className="text-gray-400 text-xs">
                                {device.type}
                                {device.is_active && ' • Active'}
                              </p>
                            </div>

                            {/* Active Indicator */}
                            {device.is_active && (
                              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <Music size={32} className="mx-auto text-gray-600 mb-2" />
                          <p className="text-gray-400 text-sm">No devices found</p>
                          <p className="text-gray-500 text-xs mt-1">Open Spotify on your device</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default GlobalMediaPlayerBar;
